import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { X, Cloud, CloudOff, CheckCircle2, XCircle, Loader2, Save, FolderPlus, KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function CommessaNextcloudModal({ commessa, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [debugging, setDebugging] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [serverState, setServerState] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const [form, setForm] = useState({
    base_url: "",
    username: "",
    password: "",
    folder: "",
    use_custom_credentials: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/commesse/${commessa.id}/nextcloud`);
      setServerState(data);
      setForm({
        base_url: data.commessa_config.base_url || "",
        username: data.commessa_config.username || "",
        password: "",
        folder: data.commessa_config.folder || "",
        use_custom_credentials: data.commessa_config.use_custom_credentials || false,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); fetchJob(); return () => stopPolling(); }, [commessa.id]);

  const fetchJob = async () => {
    try {
      const { data } = await api.get(`/commesse/${commessa.id}/nextcloud/job`);
      if (data && data.id) {
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          stopPolling();
          if (data.status === "done") {
            toast.success(`Cartella commessa creata: ${data.result?.folder || ""}`);
            await load();
          } else {
            toast.error(`Aruba Drive: ${data.error || "errore sconosciuto"}`);
          }
        }
      }
    } catch {}
  };

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(fetchJob, 1500);
  };
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (job && (job.status === "queued" || job.status === "running")) startPolling();
    else stopPolling();
    // eslint-disable-next-line
  }, [job?.status]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      const { data } = await api.put(`/commesse/${commessa.id}/nextcloud`, form);
      setServerState(data);
      setForm({ ...form, password: "" });
      toast.success("Configurazione commessa salvata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/commesse/${commessa.id}/nextcloud/test`);
      setTestResult(data);
      if (data.ok) toast.success(`Connessione OK (credenziali ${data.source_creds})`);
      else toast.error(data.message);
    } catch (err) {
      setTestResult({ ok: false, message: formatApiError(err) });
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const createFolder = async () => {
    setDebugInfo(null);
    try {
      const { data } = await api.post(`/commesse/${commessa.id}/nextcloud/folder`);
      toast.info(data.message || "Creazione cartella avviata");
      setJob({ id: data.job_id, kind: "commessa_folder", status: "queued", progress: 0, total: 0 });
      window.dispatchEvent(new Event("nurel:job-fired"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dismissJob = () => setJob(null);
  const isCreatingFolder = job && job.kind === "commessa_folder" && (job.status === "queued" || job.status === "running");

  const runDebug = async () => {
    setDebugging(true);
    setDebugInfo(null);
    try {
      const { data } = await api.post(`/commesse/${commessa.id}/nextcloud/debug`);
      setDebugInfo(data);
      if (data.mkdir_ok) toast.success("Debug completato: creazione OK");
      else toast.error("Debug completato: creazione fallita - leggi i dettagli sotto");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDebugging(false);
    }
  };

  const resolved = serverState?.resolved;
  const useCustom = form.use_custom_credentials;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="card-shell w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="commessa-nextcloud-modal">
        <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#E8F3EB", color: "var(--primary)" }}>
              <Cloud className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Aruba Drive / Nextcloud</h2>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Commessa: <strong>{commessa.name}</strong></div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
        ) : (
          <form onSubmit={save} className="p-6 space-y-5">
            {/* Status risolto */}
            <div className="p-3 rounded-md text-xs leading-relaxed" style={{ background: resolved?.configured ? "#E8F3EB" : "#FFF4E5", color: resolved?.configured ? "#1E4620" : "#8A4D0F" }}>
              {resolved?.configured ? (
                <>
                  <CheckCircle2 className="inline w-4 h-4 mr-1" /> Configurazione attiva.<br />
                  Credenziali: <strong>{resolved.source_creds}</strong> ({resolved.username}) • Cartella: <code>{resolved.folder || "(radice)"}</code>
                </>
              ) : (
                <><AlertCircle className="inline w-4 h-4 mr-1" /> Non configurata. Imposta almeno le credenziali globali oppure personalizzate qui.</>
              )}
            </div>

            {/* Folder */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Cartella della commessa</label>
              <input
                value={form.folder}
                onChange={(e) => setForm({ ...form, folder: e.target.value })}
                placeholder="Es: ProgettoAlfa/2026"
                data-testid="cm-folder"
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
              />
              <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                All'interno verrà creata la sottocartella <code>Clienti/{"{NomeCognome}_CL-XXXXX}"}</code>.
                Lascia vuoto per usare la cartella radice globale.
              </div>
            </div>

            {/* Toggle custom credentials */}
            <label className="flex items-start gap-2 text-sm p-3 rounded-md border cursor-pointer" style={{ borderColor: "var(--border)" }}>
              <input
                type="checkbox"
                checked={form.use_custom_credentials}
                onChange={(e) => setForm({ ...form, use_custom_credentials: e.target.checked })}
                className="w-4 h-4 accent-[var(--primary)] mt-0.5"
                data-testid="cm-use-custom"
              />
              <div className="flex-1">
                <div className="font-medium">Usa credenziali Aruba dedicate per questa commessa</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Utile se il cliente finale mette a disposizione il proprio spazio. Se disattivo, uso le credenziali globali.
                </div>
              </div>
            </label>

            {/* Custom credentials fields */}
            {useCustom && (
              <div className="space-y-4 pl-4 border-l-2" style={{ borderColor: "var(--primary)" }}>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>URL Aruba Drive *</label>
                  <input
                    required={useCustom}
                    value={form.base_url}
                    onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                    placeholder="https://nc-xxxxx.arubadrive.it"
                    data-testid="cm-url"
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                    style={{ borderColor: "var(--border)", background: "#fff" }}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Username *</label>
                  <input
                    required={useCustom}
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="utente@esempio.it"
                    data-testid="cm-user"
                    className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none"
                    style={{ borderColor: "var(--border)", background: "#fff" }}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    Password app {serverState?.commessa_config?.password_set && <span className="text-[color:var(--text-muted)] normal-case ml-1">— presente, lascia vuoto per mantenerla</span>}
                  </label>
                  <div className="mt-1 relative">
                    <KeyRound className="w-4 h-4 absolute left-3 top-2.5" style={{ color: "var(--text-muted)" }} />
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={serverState?.commessa_config?.password_set ? "••••••••••" : "Password app Aruba"}
                      required={useCustom && !serverState?.commessa_config?.password_set}
                      data-testid="cm-password"
                      className="w-full pl-9 pr-3 py-2 text-sm rounded-md border outline-none"
                      style={{ borderColor: "var(--border)", background: "#fff" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {testResult && (
              <div
                className="p-3 rounded-md text-sm flex items-center gap-2"
                style={testResult.ok ? { background: "#E8F3EB", color: "#1E4620" } : { background: "#FFEBEB", color: "#B30000" }}
                data-testid="cm-test-result"
              >
                {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                <span className="flex-1">{testResult.message}</span>
                {testResult.source_creds && <span className="text-xs opacity-70">via {testResult.source_creds}</span>}
              </div>
            )}

            {job && (
              <div
                className="p-3 rounded-md text-xs flex items-center gap-2"
                style={{
                  background: job.status === "failed" ? "#FDECEA" : isCreatingFolder ? "#FFF8E1" : "#E6F4EA",
                  color: job.status === "failed" ? "#B30000" : isCreatingFolder ? "#7A4500" : "#1B5E20",
                }}
                data-testid="cm-job-banner"
              >
                {isCreatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : job.status === "done" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                <div className="flex-1">
                  <div className="font-semibold">
                    Creazione cartella commessa · {job.status === "queued" && "in coda…"}
                    {job.status === "running" && "in corso…"}
                    {job.status === "done" && "completata"}
                    {job.status === "failed" && "fallita"}
                  </div>
                  {job.status === "failed" && job.error && <div className="opacity-80 mt-0.5">{job.error}</div>}
                  {job.status === "done" && job.result?.folder && <div className="opacity-80 mt-0.5">Cartella: <code>{job.result.folder}</code></div>}
                </div>
                {!isCreatingFolder && (
                  <button type="button" onClick={dismissJob} className="p-1 rounded hover:bg-black/5" data-testid="cm-job-dismiss"><X className="w-3.5 h-3.5" /></button>
                )}
                {isCreatingFolder && (
                  <span className="opacity-80 italic">Puoi continuare a lavorare</span>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="cm-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
              </button>
              <button type="button" onClick={test} disabled={testing || !resolved?.configured} className="btn-secondary" data-testid="cm-test">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />} Testa
              </button>
              <button
                type="button"
                onClick={createFolder}
                disabled={isCreatingFolder || !resolved?.configured || !resolved?.folder}
                className="btn-accent"
                data-testid="cm-create-folder"
                title={!resolved?.folder ? "Imposta una cartella commessa" : "Crea la cartella su Aruba Drive"}
              >
                {isCreatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />} Crea cartella
              </button>
              <button
                type="button"
                onClick={runDebug}
                disabled={debugging || !resolved?.configured || !resolved?.folder}
                className="btn-secondary"
                data-testid="cm-debug"
                title="Diagnostica dettagliata: mostra URL, status HTTP e messaggi di ogni step"
              >
                {debugging ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />} Diagnostica
              </button>
              <button type="button" onClick={onClose} className="btn-secondary ml-auto">Chiudi</button>
            </div>

            {debugInfo && (
              <div className="mt-4 p-3 rounded-md text-xs border" style={{ borderColor: "var(--border)", background: "var(--surface-muted)" }} data-testid="debug-output">
                <div className="font-bold mb-2 text-sm" style={{ color: "var(--text)" }}>Diagnostica</div>
                <div className="space-y-1">
                  <div><span className="font-semibold">URL WebDAV:</span> <code className="break-all">{debugInfo.webdav_url}</code></div>
                  <div><span className="font-semibold">Cartella target:</span> <code>{debugInfo.target_folder}</code></div>
                  <div><span className="font-semibold">Test connessione:</span> {debugInfo.test_connection?.ok ? "✅" : "❌"} HTTP {debugInfo.test_connection?.status} — {debugInfo.test_connection?.message}</div>
                  <div><span className="font-semibold">MKCOL complessivo:</span> {debugInfo.mkdir_ok ? "✅ OK" : "❌ Fallito"}</div>
                </div>
                {debugInfo.test_connection?.attempts?.length > 0 && (
                  <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                    <div className="font-semibold mb-1">Endpoint provati:</div>
                    {debugInfo.test_connection.attempts.map((a, i) => (
                      <div key={i} className="font-mono text-[11px] py-0.5 break-all">
                        {a.status === 200 || a.status === 207 ? "✅" : "❌"} HTTP {a.status || "err"} · {a.url || a.error}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="font-semibold mb-1">Step MKCOL:</div>
                  {debugInfo.mkdir_steps?.length === 0 && <div className="text-xs italic" style={{ color: "var(--text-muted)" }}>Nessuno step eseguito (connessione fallita prima)</div>}
                  {debugInfo.mkdir_steps?.map((s, i) => (
                    <div key={i} className="flex gap-2 py-0.5 font-mono text-[11px]">
                      <span>{s.ok ? "✅" : "❌"}</span>
                      <span className="w-12">HTTP {s.status}</span>
                      <span className="flex-1 break-all">{s.path}</span>
                      <span style={{ color: s.ok ? "var(--text-muted)" : "#B30000" }}>{String(s.message).slice(0, 60)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
